import { motion } from "framer-motion";
import { Warning, Info, ArrowClockwise } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import type { FeedbackEntry } from "../../stores/context";

interface FeedbackPatternsProps {
  patterns: FeedbackEntry[];
  className?: string;
}

const FREQUENCY_ICON = {
  always: Warning,
  often: Warning,
  sometimes: Info,
} as const;

const FREQUENCY_COLOR = {
  always: "text-[var(--color-soft-red)]",
  often: "text-[var(--color-ember-orange)]",
  sometimes: "text-[var(--text-muted)]",
} as const;

function FeedbackPatterns({ patterns, className }: FeedbackPatternsProps) {
  if (patterns.length === 0) {
    return (
      <p className="text-[11px] text-[var(--text-muted)] italic">
        No past feedback patterns available yet.
      </p>
    );
  }

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {patterns.map((entry, i) => {
        const freq = (entry.frequency as keyof typeof FREQUENCY_ICON) || "sometimes";
        const Icon = FREQUENCY_ICON[freq] ?? Info;
        const colorClass = FREQUENCY_COLOR[freq] ?? FREQUENCY_COLOR.sometimes;

        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="rounded-[var(--radius-sm)] border border-[var(--border-hairline)] bg-[var(--bg-surface)] px-2.5 py-2"
          >
            <div className="flex items-start gap-2">
              <Icon size={12} weight="bold" className={cn("mt-0.5 shrink-0", colorClass)} />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-medium text-[var(--text-primary)]">
                  {entry.pattern}
                </p>
                <p className="text-[10px] text-[var(--text-muted)] mt-0.5 flex items-center gap-1">
                  <ArrowClockwise size={9} weight="bold" />
                  {entry.frequency}
                </p>
                {entry.recommendation && (
                  <p className="text-[10px] text-[var(--color-cool-azure)] mt-1">
                    → {entry.recommendation}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

FeedbackPatterns.displayName = "FeedbackPatterns";

export { FeedbackPatterns };
export type { FeedbackPatternsProps };
